/**
 * WHEATLAND — roster wave entries. GENERATED BY ASSEMBLY, WRITTEN BY REVIEW.
 *
 * Geography (streets, landmarks, coordinates, adjacency) ported from the source
 * roster; airspace derived by the calibrated classifier in
 * scripts/derive-airspace.mjs; every descriptive sentence written fresh from
 * aerial-media truth and fact-checked against its generation brief. See
 * docs/geo/VISION-v3.md for the fact contract these entries were written under.
 *
 * 3 entries. Edit by hand freely — assembly never overwrites a
 * file that has drifted from its fragments without --force.
 */
import type { Location } from "../types";

export const ROSTER_WHEATLAND: Location[] = [
// ──────────────────────────────────────────────────────────────── HUSSAR ──
  {
    slug: "hussar",
    name: "Hussar",
    region: "wheatland",
    city: "Hussar",
    province: "Alberta",
    coordinates: { lat: 51.0833, lng: -112.6833 },
    tier: 4,
    driveMinutesFromCalgary: 85,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Beiseker aerodrome",
      distanceToAerodromeKm: 64,
      authorizationRequired: false,
      authorizationNote:
        "In uncontrolled Class G airspace about 64 km from Beiseker aerodrome, Hussar lies outside the charted Calgary-area control zones, and no authorisation is normally required. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "Hussar's light works in the horizontal. The country here is level enough that the last hour of sun travels across it rather than down onto it, and the Hussar Grain Elevator casts the longest shadow in the district.",
      consequence:
        "Shot at midday, the same country flattens into one unbroken tone — a farmyard, a machinery yard or the elevator itself photographs as beige on beige, and the client pays for a frame with no depth in it.",
      resolution:
        "Hussar shoots are scheduled into the low-sun windows: a golden-hour hold at altitude lets the raking light pull texture out of the fields and separation out of every building, delivered in the standard turnaround.",
    },
    landmarks: [
      "Hussar Grain Elevator",
      "Hussar Community Hall",
      "Hussar Golf Club",
      "Main Street",
      "Range Road 220",
    ],
    terrain:
      "Open grain country on the level east side of Wheatland County. Sections of crop run to the horizon in every direction, and the village occupies a few compact blocks along Main Street with the elevator marking it from miles out.",
    windNote:
      "The eastern reach of the chinook belt. West wind arrives over open sections with nothing between the foothills and Hussar to slow it, so gust spread rather than average speed is the flight-planning number here.",
    seasonalWindow:
      "Harvest is the aerial calendar's peak — late-summer swathing and combining draw lines across the sections that exist at no other time of year. Snow simplifies the same fields to graphic black and white, and low golden-hour light does the most work in both seasons.",
    bylawNote:
      "Village and Wheatland County rules govern takeoff and landing on municipal land, so launches are staged from private property with the owner's permission as the default.",
    subjectMix: ["agricultural", "industrial-yard", "municipal-infrastructure"],
    mobilisationNote:
      "About 85 minutes east of the Calgary base, so Hussar is a planned mobilisation rather than a same-day add-on. It pairs naturally with Standard, Rosebud or Strathmore, which puts two or three shoots on one trip.",
    faqs: [
      {
        question: "Does drone photography in Hussar require airspace authorisation?",
        answer:
          "Normally no. Hussar sits in uncontrolled Class G airspace, well outside the charted Calgary-area control zones and about 64 km from Beiseker aerodrome, the nearest listed aerodrome. No airspace authorisation is normally required, and the specific site and altitude are still confirmed against NAV Drone before every flight.",
      },
      {
        question: "Is Hussar close enough to Calgary for a single-visit shoot?",
        answer:
          "Yes, but as a planned trip rather than a quick add-on — the drive is about 85 minutes each way. A Hussar booking pairs naturally with Standard, Rosebud or Strathmore on the same mobilisation, which spreads the travel across two or three shoots and keeps each one economical.",
      },
      {
        question: "When is the best light for aerial work around Hussar?",
        answer:
          "The first and last hour of sun. The land around Hussar is level, so midday overhead light flattens it into a single tone. Low-angle light rakes across the fields, pulls texture out of crop and stubble, and gives the Hussar Grain Elevator a shadow long enough to anchor a frame.",
      },
    ],
    primaryKeywords: [
      "drone photography Hussar",
      "aerial photography Hussar",
      "Hussar drone services",
    ],
    nearestLocations: ["strathmore", "standard", "rosebud"],
  },

  // ─────────────────────────────────────────────────────────────── ROSEBUD ──
  {
    slug: "rosebud",
    name: "Rosebud",
    region: "wheatland",
    city: "Rosebud",
    province: "Alberta",
    coordinates: { lat: 51.2333, lng: -112.95 },
    tier: 4,
    driveMinutesFromCalgary: 70,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Beiseker aerodrome",
      distanceToAerodromeKm: 40,
      authorizationRequired: false,
      authorizationNote:
        "Rosebud lies outside the charted Calgary-area control zones in uncontrolled Class G airspace, about 40 km from Beiseker aerodrome. No airspace authorisation is normally required. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "The Rosebud River does not cross this prairie so much as sink into it. The hamlet sits down inside the river's coulee, and from the air the green of the valley floor is the one interruption in the surrounding grain.",
      consequence:
        "From street level inside the valley, that setting is invisible. A ground photographer shooting the Rosebud Theatre or the Rosebud Country Inn delivers buildings without the landscape their visitors come through, which is the harder half of the sale.",
      resolution:
        "A rising reveal from the valley floor to the prairie rim restores the setting — the hamlet appears seated in the river's bend as the frame climbs — delivered in the standard turnaround.",
    },
    landmarks: [
      "Rosebud River",
      "Rosebud Theatre",
      "Rosebud Country Inn",
      "Wheatsheaf Avenue",
    ],
    terrain:
      "A hamlet folded into the Rosebud River valley, where coulee walls step down from level grain fields to a treed valley floor. The drop is invisible from the road until it tips over the rim.",
    windNote:
      "Chinook-belt country with a local complication: the Rosebud valley bends the wind that crosses it, so conditions on the valley floor and on the rim can differ on the same afternoon. Both are checked before launch.",
    seasonalWindow:
      "The valley floor greens up ahead of the fields above it and holds that contrast through summer, so the green-against-gold months give the strongest frames. North-facing coulee slopes keep snow after the open prairie has cleared, which extends winter texture here.",
    bylawNote:
      "The municipality's park and roadway rules govern launching from public land in and around the hamlet; launches default to private property with the owner's consent.",
    subjectMix: ["agricultural", "commercial-retail", "municipal-infrastructure"],
    mobilisationNote:
      "About 70 minutes from the Calgary base — a comfortable single-day booking. Rosebud pairs naturally with Standard, Strathmore or Hussar when a trip needs to carry more than one shoot.",
    faqs: [
      {
        question: "Can you fly a drone at Rosebud without NAV CANADA approval?",
        answer:
          "In most cases, yes. Rosebud lies in uncontrolled Class G airspace, outside the charted Calgary-area control zones, with the nearest listed aerodrome at Beiseker about 40 km away. Routine flights need no authorisation, though the exact site and altitude are checked against NAV Drone before every flight.",
      },
      {
        question: "Why does Rosebud photograph better from the air than from the street?",
        answer:
          "Because the setting is the story. Rosebud sits down inside the Rosebud River valley, and from street level the valley walls hide the one fact that defines the place. An aerial frame shows the hamlet seated in the river's bend below the prairie, which no ground camera can do.",
      },
      {
        question: "Can a Rosebud shoot be booked as a day trip from Calgary?",
        answer:
          "Comfortably. Rosebud is about 70 minutes from the Calgary base, close enough for a single-day booking around a stable weather window. It also pairs naturally with Standard, Strathmore or Hussar on the same mobilisation, so a venue and a nearby property can share one trip's costs.",
      },
    ],
    primaryKeywords: [
      "drone photography Rosebud",
      "aerial photography Rosebud",
      "Rosebud drone services",
    ],
    nearestLocations: ["standard", "strathmore", "hussar"],
  },

  // ────────────────────────────────────────────────────────────── STANDARD ──
  {
    slug: "standard",
    name: "Standard",
    region: "wheatland",
    city: "Standard",
    province: "Alberta",
    coordinates: { lat: 51.1503, lng: -113.0819 },
    tier: 4,
    driveMinutesFromCalgary: 65,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Beiseker aerodrome",
      distanceToAerodromeKm: 37,
      authorizationRequired: false,
      authorizationNote:
        "Standard sits in uncontrolled Class G airspace, clear of the charted Calgary-area control zones and about 37 km from Beiseker aerodrome. Authorisation is not normally required here. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "Standard is a railway plat and still reads as one from altitude: Railway Avenue sets the village's long axis, Centre Street crosses it, and the whole grid resolves in a few blocks before the fields take over.",
      consequence:
        "At ground level that compactness works against a client. A photograph of a business on Centre Street looks like a storefront anywhere, and the fact a buyer or visitor actually needs — where the village sits in its working landscape — never makes the frame.",
      resolution:
        "An approach run flown down the grid's long axis carries the viewer from open field into the village core in one move, placing the property inside the whole community — delivered in the standard turnaround.",
    },
    landmarks: [
      "Railway Avenue",
      "Centre Street",
      "Village of Standard office",
      "Standard ball diamonds",
    ],
    terrain:
      "Level grain country with the village set against it as a tight rectangular grid. From above, the streets end cleanly at field edge and the section lines carry on in every direction.",
    windNote:
      "Open chinook-belt prairie. What the forecast calls a moderate west wind reaches Standard unbroken, so marginal days are judged on gust spread at the site rather than on the regional average.",
    seasonalWindow:
      "Long light suits the grid: early and late sun rakes down Railway Avenue and gives the rooflines shadow and depth that vanish by mid-morning. Seeding and harvest bookend the strongest aerial months, when machinery and swath lines animate the fields around the village.",
    bylawNote:
      "Village of Standard rules govern takeoff and landing on municipal land such as parks and road allowances, so launches are staged from private property with the owner's permission.",
    subjectMix: ["agricultural", "municipal-infrastructure", "industrial-yard"],
    mobilisationNote:
      "About 65 minutes from the Calgary base — close enough that a same-day booking is realistic in a stable window. Standard pairs naturally with Rosebud, Hussar or Strathmore on a single mobilisation.",
    faqs: [
      {
        question: "Is airspace authorisation needed for drone work in Standard?",
        answer:
          "No, not normally. Standard sits in uncontrolled Class G airspace, clear of the charted Calgary-area control zones and roughly 37 km from Beiseker aerodrome. Flights here are routine under the RPAS rules, and the specific site and altitude are still verified against NAV Drone before every flight.",
      },
      {
        question: "What does aerial photography show in a village the size of Standard?",
        answer:
          "Context that street photography cannot reach. One frame from altitude holds the whole village — the grid off Railway Avenue, the ball diamonds at its edge, and the farmland that starts where the streets stop. For a business or yard here, that single frame explains location better than any map.",
      },
      {
        question: "How far is Standard from your Calgary base?",
        answer:
          "About 65 minutes by road. A same-day booking is realistic when the forecast holds, and a Standard visit pairs naturally with Rosebud, Hussar or Strathmore on the same trip, so neighbouring clients can split one mobilisation across two or three shoots.",
      },
    ],
    primaryKeywords: [
      "drone photography Standard",
      "aerial photography Standard",
      "Standard drone services",
    ],
    nearestLocations: ["strathmore", "hussar", "rosebud"],
  }
];
